import { useState, useEffect, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Settings,
  Globe,
  Bell,
  Cloud,
  Save,
  Loader2,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { TimezoneCombobox } from "@/components/ui/timezone-combobox";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { isSuperAdmin } from "@/lib/roles";
import { ReadOnlyBadge } from "@/components/portal/ReadOnly";
import { DatabaseAccessSection } from "@/components/portal/platform/DatabaseAccessSection";
import { ConnectorDefaultsSection } from "@/components/portal/platform/ConnectorDefaultsSection";
import { DatabaseConnectionsSection } from "@/components/portal/platform/DatabaseConnectionsSection";
import {
  platformApi,
  PlatformSettings,
  OrganisationSettings,
} from "@/services/api/platform";
import { useCountries } from "@/hooks/useCountries";
import { countryLabel, resolveVatTreatment, vatPricingHint } from "@/lib/countries";

export const Route = createFileRoute("/portal/platform/settings")({
  component: PlatformSettingsPage,
});


// ============================================================================
// Organisation Settings Panel
// ============================================================================

function OrgSettingsPanel({
  canEditSettings,
}: {
  canEditSettings: boolean;
}) {
  const { token, session } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [settings, setSettings] = useState<OrganisationSettings>({
    id: "",
    name: "",
    slug: "",
    description: "",
    timezone: "UTC",
  });

  const [originalSettings, setOriginalSettings] =
    useState<OrganisationSettings | null>(null);

  const organisationId = selectedOrganisation?.organisationId;

  const { options: countryOptions, countries: countryRows } = useCountries();

  /**
   * The VAT treatment actually in force for this organisation.
   *
   * This panel used to read `country_code === "ZA" ? "include 15% VAT" :
   * "exclude VAT"` — a fifth hardcoded copy of a rule that belongs to
   * `cp01.Country`, and wrong for every foreign org twice over: it ignored the
   * org's own override, and it called a zero-rated export "excludes VAT". The
   * fields are read-only here; the treatment is set on the platform billing
   * page.
   */
  const vat = resolveVatTreatment(
    countryRows,
    settings.country_code,
    settings.vat_treatment,
  );

  // Read the token via a ref so an auth-token rotation does NOT re-run the load
  // effect — which flips `loading` true (unmounting the form) and calls
  // setSettings(...), wiping the user's unsaved org-settings edit. An
  // organisation change still reloads (the correct trigger) via the dep below.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (tokenRef.current && organisationId) {
      loadSettings();
    }
    // token read via tokenRef so rotation doesn't reload; only org change reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  const loadSettings = async () => {
    const currentToken = tokenRef.current;
    if (!currentToken || !organisationId) return;
    setLoading(true);
    try {
      const response = await platformApi.getOrganisationSettings(
        currentToken,
        organisationId,
      );
      setSettings(response);
      setOriginalSettings(response);
    } catch {
      toast.error("Failed to load organisation settings");
      setSettings((prev) => ({
        ...prev,
        id: organisationId,
        name: selectedOrganisation?.organisationName || "",
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!token || !organisationId) return;
    if (!settings.name.trim()) {
      toast.error("Organisation name is required");
      return;
    }

    setSaving(true);
    try {
      const response = await platformApi.updateOrganisationSettings(
        token,
        organisationId,
        {
          name: settings.name,
          description: settings.description,
          timezone: settings.timezone,
        },
      );
      setSettings(response);
      setOriginalSettings(response);
      setIsEditing(false);
      toast.success("Organisation settings saved successfully");
    } catch {
      toast.error("Failed to save organisation settings");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalSettings) {
      setSettings(originalSettings);
    }
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <ReadOnlyBadge show={!canEditSettings} />
        {canEditSettings &&
          (isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)}>Edit Settings</Button>
          ))}
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              General Information
            </CardTitle>
            <CardDescription className="text-xs">
              Basic organisation details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Organisation Name</Label>
                <Input
                  value={settings.name}
                  onChange={(e) =>
                    setSettings({ ...settings, name: e.target.value })
                  }
                  disabled={!isEditing}
                />
              </div>
              <div className="space-y-2">
                <Label>Slug (Permalink)</Label>
                <Input value={settings.slug} disabled className="bg-muted" />
                <p className="text-xs text-muted-foreground">
                  Used in URLs and API calls (cannot be changed)
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={settings.description}
                onChange={(e) =>
                  setSettings({ ...settings, description: e.target.value })
                }
                placeholder="Brief description of your organisation..."
                disabled={!isEditing}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Regional Settings
            </CardTitle>
            <CardDescription className="text-xs">
              Timezone, country, and billing preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Timezone</Label>
                <TimezoneCombobox
                  token={token}
                  value={settings.timezone}
                  country={settings.country_code || undefined}
                  onValueChange={(tz) =>
                    setSettings({ ...settings, timezone: tz })
                  }
                  disabled={!isEditing}
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input
                  value={
                    countryLabel(settings.country_code, countryOptions) || "—"
                  }
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  Set by platform administrator
                </p>
              </div>
              <div className="space-y-2">
                <Label>Billing Currency</Label>
                <Input
                  value={
                    settings.billing_currency === "ZAR"
                      ? "ZAR (South African Rand)"
                      : settings.billing_currency === "USD"
                        ? "USD (US Dollar)"
                        : settings.billing_currency || "ZAR"
                  }
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  {vatPricingHint(vat.treatment, vat.rate)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {isSuperAdmin(session) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Organisation Info</CardTitle>
              <CardDescription className="text-xs">
                Read-only organisation information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Organisation ID</Label>
                  <Input
                    value={settings.id || organisationId || ""}
                    disabled
                    className="bg-muted font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Input
                    value={selectedOrganisation?.status || "Active"}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
}

// ============================================================================
// Platform Settings Panel (superadmin only)
// ============================================================================

function PlatformSettingsPanel() {
  const { token } = useAuth();
  const { can } = usePermissions();
  const canEditSettings = can("edit", "settings");
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [settings, setSettings] = useState<PlatformSettings>({
    platform_name: "",
    platform_url: "",
    support_email: "",
    default_timezone: "UTC",
    description: "",
  });

  const [originalSettings, setOriginalSettings] =
    useState<PlatformSettings | null>(null);

  // Read the token via a ref so an auth-token rotation does NOT re-run the load
  // effect (which flips `loading` true → unmounts the form, and calls
  // setSettings(...) → wipes unsaved platform-settings edits). Platform
  // settings are global; loaded once on mount behind the auth gate.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (tokenRef.current) {
      loadSettings();
    }
    // token read via tokenRef so rotation doesn't reload; load once on mount.

  }, []);

  const loadSettings = async () => {
    const currentToken = tokenRef.current;
    if (!currentToken) return;
    setLoading(true);
    try {
      const response = await platformApi.getPlatformSettings(currentToken);
      setSettings(response);
      setOriginalSettings(response);
    } catch {
      toast.error("Failed to load platform settings");
      setSettings({
        platform_name: "Larasoft Platform",
        platform_url: "https://platform.larasoft.ai",
        support_email: "support@larasoft.ai",
        default_timezone: "UTC",
        description:
          "AI-powered enterprise platform for document processing and intelligent automation.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!token) return;
    if (!settings.platform_name.trim()) {
      toast.error("Platform name is required");
      return;
    }

    setSaving(true);
    try {
      const response = await platformApi.updatePlatformSettings(
        token,
        settings,
      );
      setSettings(response);
      setOriginalSettings(response);
      setIsEditing(false);
      toast.success("Platform settings saved successfully");
    } catch {
      toast.error("Failed to save platform settings");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalSettings) {
      setSettings(originalSettings);
    }
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <ReadOnlyBadge show={!canEditSettings} />
        {canEditSettings &&
          (isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)}>Edit Settings</Button>
          ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            className="flex items-center gap-2"
          >
            <Bell className="h-4 w-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <Cloud className="h-4 w-4" />
            Integrations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">General Settings</CardTitle>
              <CardDescription className="text-xs">
                Basic platform configuration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Platform Name</Label>
                  <Input
                    value={settings.platform_name}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        platform_name: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Platform URL</Label>
                  <Input
                    value={settings.platform_url}
                    onChange={(e) =>
                      setSettings({ ...settings, platform_url: e.target.value })
                    }
                    disabled={!isEditing}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Support Email</Label>
                  <Input
                    type="email"
                    value={settings.support_email}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        support_email: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Default Timezone</Label>
                  <TimezoneCombobox
                    token={token}
                    value={settings.default_timezone}
                    onValueChange={(tz) =>
                      setSettings({ ...settings, default_timezone: tz })
                    }
                    disabled={!isEditing}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Platform Description</Label>
                <Textarea
                  value={settings.description}
                  onChange={(e) =>
                    setSettings({ ...settings, description: e.target.value })
                  }
                  disabled={!isEditing}
                />
              </div>
            </CardContent>
          </Card>

        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Notification Settings</CardTitle>
              <CardDescription className="text-xs">
                Configure platform notification behavior
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Email Notifications</Label>
                  <p className="text-xs text-muted-foreground">
                    Send email notifications
                  </p>
                </div>
                <Switch checked disabled={!isEditing} />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Admin Alerts</Label>
                  <p className="text-xs text-muted-foreground">
                    Alert admins on critical events
                  </p>
                </div>
                <Switch checked disabled={!isEditing} />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Usage Alerts</Label>
                  <p className="text-xs text-muted-foreground">
                    Alert on usage threshold breaches
                  </p>
                </div>
                <Switch disabled={!isEditing} />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Security Alerts</Label>
                  <p className="text-xs text-muted-foreground">
                    Alert on security events
                  </p>
                </div>
                <Switch checked disabled={!isEditing} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integrations" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">External Integrations</CardTitle>
              <CardDescription className="text-xs">
                Configure third-party service integrations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <Cloud className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No integrations configured</p>
                <p className="text-xs mt-2">
                  Add integrations like Slack, Microsoft Teams, or webhooks
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// Main Settings Page
// ============================================================================

function PlatformSettingsPage() {
  const { session } = useAuth();
  const { can } = usePermissions();
  const isUserSuperAdmin = isSuperAdmin(session);
  const canEditSettings = can("edit", "settings");

  if (isUserSuperAdmin) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage organisation and platform settings
          </p>
        </div>

        <Tabs defaultValue="org-settings">
          <TabsList>
            <TabsTrigger value="org-settings">
              Organisation Settings
            </TabsTrigger>
            <TabsTrigger value="platform-settings">
              Platform Settings
            </TabsTrigger>
            <TabsTrigger value="connector-defaults">
              Connector defaults
            </TabsTrigger>
          </TabsList>
          <TabsContent value="org-settings" className="mt-6 space-y-6">
            <OrgSettingsPanel canEditSettings={canEditSettings} />
            <DatabaseAccessSection />
            <DatabaseConnectionsSection />
          </TabsContent>
          <TabsContent value="platform-settings" className="mt-6">
            <PlatformSettingsPanel />
          </TabsContent>
          <TabsContent value="connector-defaults" className="mt-6">
            <ConnectorDefaultsSection />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="h-6 w-6" />
          Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your organisation settings
        </p>
      </div>

      <OrgSettingsPanel canEditSettings={canEditSettings} />
      <DatabaseAccessSection />
      <DatabaseConnectionsSection />
    </div>
  );
}
